import {
    Form, FormButton, FormButtonContainer, FormItem, FormItemType, FormSubmitHandler, TextInput, useAlerts, useForm,
} from "@sis/react-library"
import { ExampleDto } from "./FormSamplesValidationPage";
import { Grid } from "@mui/material";

/////////////////////////////////////////////////////////////////////////////////////////////
// Interface for the form (see basic usage example for more information).
/////////////////////////////////////////////////////////////////////////////////////////////
// allow no members for shorter name to be more readable below
// eslint-disable-next-line
interface T extends ExampleDto { }

interface FormSamplesValidationFormProps {
    onSubmit: FormSubmitHandler<ExampleDto>;
    submitting: boolean;
}

export const FormSamplesValidationForm = ({
    onSubmit, submitting
}: FormSamplesValidationFormProps) => {

    //////////////////////////////////////////////////////////////////////////////////////////////////
    // No need to use state at all in this component, 
    // and the controls (TextInput for example) doesn't need to add onClick or set the value.
    // This is all done internally within the form components removing boilder plate code
    //////////////////////////////////////////////////////////////////////////////////////////////////    

    //////////////////////////////////////////////////////////////////////////////////////////////////
    // The context provider holds information for multiple forms, so a form name needs to be specified
    // and it needs to be unique
    //////////////////////////////////////////////////////////////////////////////////////////////////
    const formName = 'FormValidation';

    const { resetForm } = useForm<T>(formName);
    const { confirm } = useAlerts();

    const handleCancel = () => {
        confirm('Cancel', 'Are you sure you want to cancel and loose your changes?',
            () => { resetForm() },
            "Yes", "No"
        );
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////
    // You can add your own custom validation
    // the function will take the value of the input control as a parameter
    // the function needs to return true (passed) or false (failed)
    //////////////////////////////////////////////////////////////////////////////////////////////////
    const customValidationRule = (val: FormItemType) => {
        return val === "abcd";
    }

    return (
        <Form<T>
            name={formName}
            onSubmit={onSubmit}
            submitting={submitting}
            beforeLeaveText='You have made changes, are you sure you want to leave ?'
        >
            <FormItem<T>
                formField="required"
                label="Is required"
                rules={[{ type: "required", message: "This field is required!" }]}
                element={<TextInput maxLength={20} />}
            />
            <FormItem<T>
                formField="number"
                label="Number"
                rules={[{ type: "number", message: "Must be a number!" }]}
                element={<TextInput maxLength={20} />}
            />
            <FormItem<T>
                formField="email"
                label="Email"
                rules={[{ type: "email", message: "Must be an email address!" }]}
                element={<TextInput maxLength={50} />}
            />
            <Grid container spacing={2}>
                <Grid size={{ md: 6 }}>
                    <FormItem<T>
                        formField="minLength"
                        label="Must be at least 3 characters!"
                        rules={[{ type: "minLength", value: 3, message: "Must be at least 3 characters!" }]}
                        element={<TextInput maxLength={20} />}
                    />
                </Grid>
                <Grid size={{ md: 6 }}>
                    <FormItem<T>
                        formField="maxLength"
                        label="Must be less than 10 characters!"
                        rules={[{ type: "maxLength", value: 10, message: "Must be 10 or less characters!" }]}
                        element={<TextInput maxLength={20} />}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ md: 6 }}>
                    <FormItem<T>
                        formField="minValue"
                        label="Must be the number 20 or greater!"
                        rules={[
                            { type: "number", message: "Must be a number!" },
                            { type: "minValue", value: 20, message: "Must be 20 or greater!" }
                        ]}
                        element={<TextInput maxLength={20} />}
                    />
                </Grid>
                <Grid size={{ md: 6 }}>
                    <FormItem<T>
                        formField="maxValue"
                        label="Must be the number 40 or less!"
                        rules={[
                            { type: "number", message: "Must be a number!" },
                            { type: "maxValue", value: 40, message: "Must be 40 or less!" }
                        ]}
                        element={<TextInput maxLength={20} />}
                    />
                </Grid>
            </Grid>
            <FormItem<T>
                formField="minAndMaxValue"
                label="Must be required and between 50 and 150!"
                rules={[
                    { type: "required", message: "This field is required!" },
                    { type: "number", message: "Must be a number!" },
                    { type: "minValue", value: 51, message: "Must be greater than 50!" },
                    { type: "maxValue", value: 149, message: "Must be less than 150!" }
                ]}
                element={<TextInput maxLength={20} />}
            />
            <Grid container spacing={2}>
                <Grid size={{ md: 6 }}>
                    <FormItem<T>
                        formField="password"
                        label="Please enter a password!"
                        rules={[{ type: "required", message: "Please enter a password!" }]}
                        element={<TextInput type="password" maxLength={20} />}
                    />
                </Grid>
                <Grid size={{ md: 6 }}>
                    <FormItem<T>
                        formField="confirmPassword"
                        label="Confirm Password"
                        rules={[
                            { type: "compareEqual", field: "password", message: "Passwords don't match!" },
                            { type: "required", message: "Please confirm your password!" }
                        ]}
                        element={<TextInput type="password" maxLength={50} />}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ md: 6 }}>
                    <FormItem<T>
                        formField="notEqualCompare1"
                        label="Compare not equal"
                        rules={[{ type: "required", message: "Please enter a value!" }]}
                        element={<TextInput maxLength={20} />}
                    />
                </Grid>
                <Grid size={{ md: 6 }}>
                    <FormItem<T>
                        formField="notEqualCompare2"
                        label="Compare not equal"
                        rules={[
                            { type: "compareNotEqual", field: "notEqualCompare1", message: "The fields must have different values!" },
                            { type: "required", message: "Please enter a value!" }
                        ]}
                        element={<TextInput maxLength={50} />}
                    />
                </Grid>
            </Grid>
            <FormItem<T>
                formField="custom"
                label="Custom: must be 'abcd'"
                rules={[
                    { type: "custom", checkRule: customValidationRule, message: "The value be 'abcd'!" }
                ]}
                element={<TextInput maxLength={50} />}
            />
            <FormButtonContainer>
                <FormButton
                    formButtonText='Cancel'
                    onClick={() => { handleCancel() }}
                />
                <FormButton
                    formButtonText='Submit'
                    submitButton
                />
            </FormButtonContainer>
        </Form>
    )
}
